import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, API_BASE, formatApiError, getToken, BRANCH_KEY } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/service-order/StatusBadge";
import { ChangeStatusDialog } from "@/components/service-order/ChangeStatusDialog";
import { DiagnosisPanel } from "@/components/service-order/DiagnosisPanel";
import { WorkOrdersPanel } from "@/components/service-order/WorkOrdersPanel";
import { EvidencesPanel } from "@/components/service-order/EvidencesPanel";
import { StatusTimeline } from "@/components/service-order/StatusTimeline";
import { ShareOrderDialog } from "@/components/service-order/ShareOrderDialog";
import { QuotesPanel } from "@/components/service-order/QuotesPanel";
import { FinancePanel } from "@/components/service-order/FinancePanel";
import { FinalSummaryPanel } from "@/components/service-order/FinalSummaryPanel";
import { useAuth } from "@/contexts/AuthContext";
import {
  SERVICE_TYPES,
  FUEL_LEVELS,
  CHECKLIST_LEVEL,
  BATTERY_LEVEL,
  labelFrom,
} from "@/lib/constants";
import {
  ArrowLeft,
  FileDown,
  RefreshCw,
  Share2,
  User2,
  Car,
  MapPin,
  Wrench,
  ClipboardCheck,
  Stethoscope,
  Camera,
  Clock,
  Info,
  FileText,
  Wallet,
  ClipboardList,
} from "lucide-react";

function Row({ label, value }) {
  return (
    <div>
      <dt className="text-[11px] uppercase text-muted-foreground tracking-wider">{label}</dt>
      <dd className="text-sm text-slate-800">{value || "—"}</dd>
    </div>
  );
}

export default function ServiceOrderDetail() {
  const { soId } = useParams();
  const navigate = useNavigate();
  const { isSuperAdmin, activeTenantId } = useTenant();
  const { session, hasPermission } = useAuth();
  const [tab, setTab] = useState("detail");
  const canAuthorizeCredit = ["super_admin", "dueno", "gerente"].includes(session?.user?.role_name);
  const [detail, setDetail] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusDlgOpen, setStatusDlgOpen] = useState(false);
  const [shareDlgOpen, setShareDlgOpen] = useState(false);

  const tenantParam = isSuperAdmin && activeTenantId ? { tenant_id: activeTenantId } : {};

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, { data: u }] = await Promise.all([
        api.get(`/service-orders/${soId}`),
        // roles without users:view (tecnico, caja) must still open the order
        api.get("/users", { params: tenantParam }).catch(() => ({ data: [] })),
      ]);
      setDetail(data);
      setUsers(u.filter((x) => x.status === "active"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soId, activeTenantId, isSuperAdmin]);

  useEffect(() => { load(); }, [load]);

  function openPdf() {
    // Authenticated PDF: we need Authorization: Bearer, so fetch-and-open
    const token = getToken();
    fetch(`${API_BASE}/service-orders/${soId}/intake.pdf`, {
      headers: { Authorization: `Bearer ${token}`, "X-Branch-Id": localStorage.getItem(BRANCH_KEY) || "" },
    })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      })
      .catch((e) => toast.error(`No se pudo generar el PDF: ${e.message}`));
  }

  if (loading || !detail) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  const {
    service_order: so, branch, customer, vehicle,
    checklist, diagnoses, work_orders, work_tasks, evidences, status_logs,
    received_by,
  } = detail;

  return (
    <div className="space-y-6" data-testid="page-service-order-detail">
      <Button variant="ghost" size="sm" onClick={() => navigate("/ordenes-de-servicio")} data-testid="so-detail-back">
        <ArrowLeft className="h-4 w-4 mr-2" /> Volver a órdenes
      </Button>

      <PageHeader
        data-testid="so-detail-header"
        title={
          <span className="flex items-center gap-3 flex-wrap">
            <span className="mono text-tenant">{so.folio}</span>
            <StatusBadge status={so.status} className="font-sans tracking-normal" data-testid="so-detail-status" />
            {so.stage_name && (
              <span className="font-sans text-sm font-medium tracking-normal text-slate-700 whitespace-pre" data-testid="so-detail-stage">
                {so.service_flow_name} · {so.stage_name}
              </span>
            )}
          </span>
        }
        description={
          <span>
            {customer?.legal_name} · {vehicle?.brand} {vehicle?.model} {vehicle?.year || ""}
            {vehicle?.plates && <> · <span className="mono">{vehicle.plates}</span></>}
            {" · "}{branch?.name}
          </span>
        }
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={load} data-testid="so-detail-refresh">
              <RefreshCw className="h-4 w-4 mr-1" /> Actualizar
            </Button>
            <Button variant="outline" size="sm" onClick={openPdf} data-testid="so-detail-pdf">
              <FileDown className="h-4 w-4 mr-1" /> PDF ingreso
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShareDlgOpen(true)}
              data-testid="so-detail-share"
            >
              <Share2 className="h-4 w-4 mr-1" /> Compartir
            </Button>
            <Button
              size="sm"
              className="btn-tenant"
              onClick={() => setStatusDlgOpen(true)}
              disabled={so.status === "cancelado" || so.status === "entregado"}
              data-testid="so-detail-change-status"
            >
              Cambiar estatus
            </Button>
          </div>
        }
      />

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><User2 className="h-4 w-4" /> Cliente</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-1">
            <div className="font-medium">{customer?.legal_name}</div>
            <div className="text-muted-foreground">{customer?.phone || "—"}</div>
            <div className="text-muted-foreground truncate">{customer?.email || ""}</div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Car className="h-4 w-4" /> Vehículo</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-1">
            <div className="font-medium">{vehicle?.brand} {vehicle?.model} {vehicle?.year || ""} {vehicle?.version || ""}</div>
            <div className="text-muted-foreground">Placas: <span className="mono">{vehicle?.plates || "—"}</span> · VIN: <span className="mono text-[11px]">{vehicle?.vin || "—"}</span></div>
            <div className="text-muted-foreground">Color: {vehicle?.color || "—"} · Km ingreso: {(so.mileage_at_entry || vehicle?.base_mileage || 0).toLocaleString("es-MX")}</div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><MapPin className="h-4 w-4" /> Recepción</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-1">
            <div className="font-medium">{branch?.name}</div>
            <div className="text-muted-foreground">
              {so.intake_date} {so.intake_time || ""}
            </div>
            <div className="text-muted-foreground">
              Recibió: {received_by?.full_name || so.intake_signature_name || "—"}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="grid grid-cols-4 md:grid-cols-8 max-w-5xl" data-testid="so-tabs">
          <TabsTrigger value="detail" data-testid="tab-detail">
            <ClipboardCheck className="h-4 w-4 md:mr-2" /> <span className="hidden md:inline">Detalle</span>
          </TabsTrigger>
          <TabsTrigger value="diagnosis" data-testid="tab-diagnosis">
            <Stethoscope className="h-4 w-4 md:mr-2" /> <span className="hidden md:inline">Diagnóstico</span>
          </TabsTrigger>
          <TabsTrigger value="work" data-testid="tab-work">
            <Wrench className="h-4 w-4 md:mr-2" /> <span className="hidden md:inline">Trabajo</span>
          </TabsTrigger>
          {hasPermission("quotes:view") && (
            <TabsTrigger value="quotes" data-testid="tab-quotes">
              <FileText className="h-4 w-4 md:mr-2" /> <span className="hidden md:inline">Cotizaciones</span>
            </TabsTrigger>
          )}
          {hasPermission("payments:view") && (
            <TabsTrigger value="finance" data-testid="tab-finance">
              <Wallet className="h-4 w-4 md:mr-2" /> <span className="hidden md:inline">Anticipos</span>
            </TabsTrigger>
          )}
          <TabsTrigger value="evidence" data-testid="tab-evidence">
            <Camera className="h-4 w-4 md:mr-2" /> <span className="hidden md:inline">Evidencias</span>
          </TabsTrigger>
          <TabsTrigger value="final-summary" data-testid="tab-final-summary">
            <ClipboardList className="h-4 w-4 md:mr-2" /> <span className="hidden md:inline">Resumen final</span>
          </TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-timeline">
            <Clock className="h-4 w-4 md:mr-2" /> <span className="hidden md:inline">Bitácora</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="detail" className="space-y-6" data-testid="tab-content-detail">
          <Card className="border-border">
            <CardHeader className="border-b border-border pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Info className="h-4 w-4" /> Datos de ingreso
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Row label="Tipo de servicio" value={labelFrom(SERVICE_TYPES, so.service_type)} />
                <Row label="Nivel combustible" value={labelFrom(FUEL_LEVELS, so.fuel_level)} />
                <Row label="Fecha" value={`${so.intake_date} ${so.intake_time || ""}`} />
                <Row label="Firma recepción" value={so.intake_signature_name} />
                <div className="col-span-2 md:col-span-4">
                  <Row label="Motivo de ingreso" value={so.entry_reason} />
                </div>
                <div className="col-span-2 md:col-span-2">
                  <Row label="Síntomas reportados" value={so.customer_reported_issue} />
                </div>
                <div className="col-span-2 md:col-span-2">
                  <Row label="Observaciones" value={so.general_condition_notes} />
                </div>
                <div className="col-span-2 md:col-span-4">
                  <Row label="Accesorios" value={so.accessories_notes} />
                </div>
              </dl>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader className="border-b border-border pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <ClipboardCheck className="h-4 w-4" /> Checklist de recepción
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              {checklist ? (
                <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Row label="Exterior" value={labelFrom(CHECKLIST_LEVEL, checklist.exterior_status)} />
                  <Row label="Interior" value={labelFrom(CHECKLIST_LEVEL, checklist.interior_status)} />
                  <Row label="Batería" value={labelFrom(BATTERY_LEVEL, checklist.battery_status)} />
                  <Row label="Llantas" value={labelFrom(CHECKLIST_LEVEL, checklist.tire_condition)} />
                  <Row label="Fugas visibles" value={checklist.visible_leaks ? "Sí" : "No"} />
                  <div className="col-span-2 md:col-span-3">
                    <Row label="Testigos" value={checklist.dashboard_lights} />
                  </div>
                  <div className="col-span-2 md:col-span-2">
                    <Row label="Accesorios presentes" value={checklist.accessories_present} />
                  </div>
                  <div className="col-span-2 md:col-span-2">
                    <Row label="Notas" value={checklist.notes} />
                  </div>
                </dl>
              ) : (
                <div className="text-sm text-muted-foreground">Sin checklist registrado.</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="diagnosis" data-testid="tab-content-diagnosis">
          <DiagnosisPanel
            serviceOrder={so}
            diagnoses={diagnoses}
            users={users}
            onChanged={load}
          />
        </TabsContent>

        <TabsContent value="work" data-testid="tab-content-work">
          <WorkOrdersPanel
            serviceOrder={so}
            workOrders={work_orders}
            workTasks={work_tasks}
            users={users}
            onChanged={load}
          />
        </TabsContent>

        <TabsContent value="evidence" data-testid="tab-content-evidence">
          <EvidencesPanel
            serviceOrder={so}
            evidences={evidences}
            onChanged={load}
          />
        </TabsContent>

        <TabsContent value="quotes" data-testid="tab-content-quotes">
          <QuotesPanel serviceOrder={so} onChanged={load} />
        </TabsContent>

        <TabsContent value="finance" data-testid="tab-content-finance">
          <FinancePanel serviceOrder={so} onChanged={load} canAuthorizeCredit={canAuthorizeCredit} />
        </TabsContent>

        <TabsContent value="final-summary" data-testid="tab-content-final-summary">
          <FinalSummaryPanel serviceOrder={so} onChanged={load} />
        </TabsContent>

        <TabsContent value="timeline" data-testid="tab-content-timeline">
          <Card className="border-border p-6">
            <StatusTimeline logs={status_logs} />
          </Card>
        </TabsContent>
      </Tabs>

      <ChangeStatusDialog
        open={statusDlgOpen}
        onOpenChange={setStatusDlgOpen}
        serviceOrder={so}
        onDone={load}
      />

      <ShareOrderDialog
        open={shareDlgOpen}
        onOpenChange={setShareDlgOpen}
        serviceOrder={so}
        onChanged={load}
      />
    </div>
  );
}
